import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { SwitchOption } from "@src/lms/jelly/extensions/ui";
import { messages } from "../Interior.messages";
import styles from "./AccordionContent.module.scss";
import { CarDiagram, CarLabelItem } from "../diagrams/CarDiagram";
import { RearRightSeatModal, RearLeftSeatModal, FrontRightSeatModal } from "../modals/Modals";
import { useBlocks } from "@jelly/lesson";
import config from "appConfig";

export const AccordionContent2: React.FC = () => {
  const { formatMessage } = useIntl();
  const [selectedOption, setSelectedOption] = useState(0);
  const [rearRightSeatModalOpen, setRearRightSeatModalOpen] = useState(false);
  const [rearLeftSeatModalOpen, setRearLeftSeatModalOpen] = useState(false);
  const [frontRightSeatModalOpen, setFrontRightSeatModalOpen] = useState(false);
  const blocks = useBlocks();

  const handleItemSelect = useCallback((item: CarLabelItem | null): void => {
    // Close all modals first
    setRearRightSeatModalOpen(false);
    setRearLeftSeatModalOpen(false);
    setFrontRightSeatModalOpen(false);

    // Open the corresponding modal
    if (item) {
      switch (item) {
        case "label-rrs":
          setRearRightSeatModalOpen(true);
          break;
        case "label-rls":
          setRearLeftSeatModalOpen(true);
          break;
        case "label-frs":
          setFrontRightSeatModalOpen(true);
          break;
      }
    }
  }, []);

  const handleCloseRearRightSeatModal = useCallback(() => {
    setRearRightSeatModalOpen(false);
  }, []);

  const handleCloseRearLeftSeatModal = useCallback(() => {
    setRearLeftSeatModalOpen(false);
  }, []);

  const handleCloseFrontRightSeatModal = useCallback(() => {
    setFrontRightSeatModalOpen(false);
  }, []);

  const renderContent = (): JSX.Element => {
    switch (selectedOption) {
      case 0:
        return (
          <div className={styles.content}>
            <div className={styles.leftColumn}>
              <div className={styles.diagram}>
                <CarDiagram onItemSelect={handleItemSelect} svgType="positions" />
              </div>
            </div>
            <div className={styles.rightColumn}>
              <h3>{formatMessage(messages.C8_P3_S1_A2_Content1_Heading)}</h3>
              <blocks.audioButton
                url={`${config.STATIC_ASSETS_URL}/lms/case/case08/interior/frs.mp3`}
                icon="cis-media-play"
                theme="secondary"
                shape="round"
                text={formatMessage(messages.C8_P3_S1_A2_Content1_Button1_Label)}
              />
              <blocks.audioButton
                url={`${config.STATIC_ASSETS_URL}/lms/case/case08/interior/rls.mp3`}
                icon="cis-media-play"
                theme="secondary"
                shape="round"
                text={formatMessage(messages.C8_P3_S1_A2_Content1_Button2_Label)}
              />
              <blocks.audioButton
                url={`${config.STATIC_ASSETS_URL}/lms/case/case08/interior/rrs.mp3`}
                icon="cis-media-play"
                theme="secondary"
                shape="round"
                text={formatMessage(messages.C8_P3_S1_A2_Content1_Button3_Label)}
              />
            </div>
          </div>
        );
      case 1:
        return (
          <div className={styles.content}>
            <div className={styles.leftColumn}>
              <CarDiagram onItemSelect={handleItemSelect} svgType="speed" />
            </div>
            <div className={styles.rightColumn}>
              <h3>{formatMessage(messages.C8_P3_S1_A2_Content2_Heading)}</h3>
              <blocks.audioButton
                url={`${config.STATIC_ASSETS_URL}/lms/case/case08/interior/smooth.mp3`}
                icon="cis-media-play"
                theme="secondary"
                shape="round"
                text={formatMessage(messages.C8_P3_S1_A2_Content2_Button1_Label)}
              />
              <blocks.audioButton
                url={`${config.STATIC_ASSETS_URL}/lms/case/case08/interior/rough.mp3`}
                icon="cis-media-play"
                theme="secondary"
                shape="round"
                text={formatMessage(messages.C8_P3_S1_A2_Content2_Button2_Label)}
              />
              <blocks.audioButton
                url={`${config.STATIC_ASSETS_URL}/lms/case/case08/interior/bars.mp3`}
                icon="cis-media-play"
                theme="secondary"
                shape="round"
                text={formatMessage(messages.C8_P3_S1_A2_Content2_Button3_Label)}
              />
            </div>
          </div>
        );
      default:
        return <div className={styles.content}>Select an option</div>;
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.contentWrapper}>{renderContent()}</div>
      <div className={styles.switchWrapper}>
        <SwitchOption
          onClick={setSelectedOption}
          activeItem={selectedOption}
          items={[
            {
              name: formatMessage(messages.C8_P3_S1_A2_Content_SwitchButton1_Label),
            },
            {
              name: formatMessage(messages.C8_P3_S1_A2_Content_SwitchButton2_Label),
            },
          ]}
        />
      </div>

      {/* Modals */}
      <RearRightSeatModal visible={rearRightSeatModalOpen} onClose={handleCloseRearRightSeatModal} />
      <RearLeftSeatModal visible={rearLeftSeatModalOpen} onClose={handleCloseRearLeftSeatModal} />
      <FrontRightSeatModal visible={frontRightSeatModalOpen} onClose={handleCloseFrontRightSeatModal} />
    </div>
  );
};
